import React, { useCallback, useEffect, useState } from "react";
import "./App.css";
import { Toaster, toast } from "sonner";
import Header from "./components/taxflow/Header";
import LandingView from "./components/taxflow/LandingView";
import WorkspaceView from "./components/taxflow/WorkspaceView";
import AnalyticsView from "./components/taxflow/AnalyticsView";
import AdminView from "./components/taxflow/AdminView";
import CopilotDrawer from "./components/taxflow/CopilotDrawer";
import UploadModal from "./components/taxflow/UploadModal";
import ReportsView from "./components/taxflow/ReportsView";
import SignOffView from "./components/taxflow/SignOffView";
import ClientPortalView from "./components/taxflow/ClientPortalView";
import FirmOverviewView from "./components/taxflow/FirmOverviewView";
import SignInView from "./components/taxflow/SignInView";
import CustomerDashboard from "./components/taxflow/CustomerDashboard";
import RequestsView from "./components/taxflow/RequestsView";
import { fetchBootstrap, setCurrentEntity } from "./api";

const STAFF_VIEWS = ["firm", "workspace", "analytics", "reports", "signoff", "portal", "admin", "requests"];

const loadAuth = () => {
  const token = localStorage.getItem("tf_token");
  const user = localStorage.getItem("tf_user");
  return token && user ? { token, user: JSON.parse(user) } : null;
};

function App() {
  const [auth, setAuth] = useState(loadAuth);
  const [view, setView] = useState(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reportsTab, setReportsTab] = useState("pl");
  const [resetToken, setResetToken] = useState(null);

  const isStaff = !!(auth && auth.user && (auth.user.role === "staff" || auth.user.role === "admin"));

  const load = useCallback(() => {
    fetchBootstrap()
      .then((d) => setData(d))
      .catch(() => setError("Unable to reach the PnLFlow engine."));
  }, []);

  useEffect(() => {
    if (isStaff) {
      setError(null);
      load();
    }
  }, [isStaff, load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get("reset_token");
    if (rt) {
      setResetToken(rt);
      setView("signin");
      return;
    }
    const a = loadAuth();
    setView(a ? (a.user.role === "customer" ? "customer" : "workspace") : "landing");
  }, []);

  const handleAuthed = useCallback((d) => {
    localStorage.setItem("tf_token", d.access_token);
    localStorage.setItem("tf_user", JSON.stringify(d.user));
    setAuth({ token: d.access_token, user: d.user });
    setView(d.user.role === "customer" ? "customer" : "workspace");
  }, []);

  const switchView = useCallback(
    (v) => {
      if (v === "reports:depreciation") {
        v = "reports";
        setReportsTab("depreciation");
      } else if (v === "reports") {
        setReportsTab("pl");
      }
      if (STAFF_VIEWS.includes(v) && !isStaff) {
        setView("signin");
        return;
      }
      if (v === "customer" && !auth) {
        setView("signin");
        return;
      }
      setView(v);
    },
    [isStaff, auth]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("tf_token");
    localStorage.removeItem("tf_user");
    setAuth(null);
    setData(null);
    setCopilotOpen(false);
    setView("landing");
    toast.success("Signed out");
  }, []);

  const handleEntityChange = useCallback(
    (slug) => {
      setCurrentEntity(slug);
      setData(null);
      load();
    },
    [load]
  );

  const applyUpdate = useCallback((patch) => {
    setData((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const slug = data ? data.entity_slug : "none";
  const staffViewActive = view && STAFF_VIEWS.includes(view) && view !== "requests";

  if (view === null) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0A0A0A] text-sm text-zinc-400">
        <span className="animate-pulse">Loading…</span>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#0A0A0A] text-zinc-200 overflow-hidden font-sans antialiased">
      <div className="h-px w-full bg-gradient-to-r from-blue-500/80 via-white/30 to-emerald-400/60 z-50 flex-shrink-0"></div>

      <div className="flex-1 flex flex-col overflow-hidden relative">
        <Header
          view={view}
          onSwitchView={switchView}
          entity={data ? data.entity_slug : ""}
          entities={data ? data.entities : []}
          onEntityChange={handleEntityChange}
          onOpenUpload={() => setUploadOpen(true)}
          onOpenCopilot={() => setCopilotOpen((p) => !p)}
          auth={auth}
          isStaff={isStaff}
          onSignOut={signOut}
        />

        {view === "landing" && <LandingView onSwitchView={switchView} onAuthed={handleAuthed} />}
        {view === "signin" && <SignInView onAuthed={handleAuthed} resetToken={resetToken} />}
        {view === "customer" && auth && (
          <CustomerDashboard user={auth.user} onNewRequest={() => switchView("landing")} />
        )}
        {view === "requests" && isStaff && <RequestsView onConverted={load} />}

        {staffViewActive && isStaff && error && (
          <div className="p-8 text-center text-sm text-rose-400" data-testid="engine-error">
            {error}
          </div>
        )}

        {staffViewActive && isStaff && !error && !data && (
          <div className="flex-1 flex items-center justify-center text-sm text-zinc-400" data-testid="engine-loading">
            <span className="animate-pulse">Loading engagement…</span>
          </div>
        )}

        {staffViewActive && isStaff && !error && data && (
          <>
            {view === "firm" && (
              <FirmOverviewView
                key={slug}
                coaOptions={data.coa_options}
                onClientCreated={() => load()}
                onClientsChanged={(changedSlug) => {
                  if (changedSlug === data.entity_slug) setCurrentEntity(null);
                  load();
                }}
                onOpenEntity={(s) => {
                  handleEntityChange(s);
                  setView("workspace");
                }}
              />
            )}
            {view === "workspace" && (
              <WorkspaceView
                key={slug}
                transactions={data.transactions}
                summary={data.summary}
                coaOptions={data.coa_options}
                targetForms={data.target_forms}
                defaultForm={data.default_form}
                onDataUpdate={applyUpdate}
                onRefresh={load}
                onSwitchView={switchView}
              />
            )}
            {view === "analytics" && <AnalyticsView key={slug} />}
            {view === "reports" && (
              <ReportsView key={`${slug}-${reportsTab}`} initialTab={reportsTab} coaOptions={data.coa_options} />
            )}
            {view === "signoff" && <SignOffView key={slug} />}
            {view === "portal" && <ClientPortalView key={slug} entity={data.entity} />}
            {view === "admin" && <AdminView key={slug} />}
          </>
        )}

        {isStaff && (
          <>
            <CopilotDrawer
              key={slug}
              open={copilotOpen}
              onClose={() => setCopilotOpen(false)}
              entity={data ? data.entity : ""}
              onNavigate={(v) => {
                switchView(v);
                setCopilotOpen(false);
              }}
            />
            <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} accounts={data ? data.accounts : []} />
          </>
        )}
      </div>

      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(17, 17, 17, 0.97)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            color: "#e4e4e7",
          },
        }}
      />
    </div>
  );
}

export default App;
